// @flow strict
import type {Graph} from 'src/types/chatbot';
import * as React from 'react';
import type {EntityType} from 'src/types/ats-entities';
import type {EntityMappingsByEntityType} from 'src/types/target-entity-types';
import type {ChatbotTableEntry} from 'src/types/email';

import {useApi} from 'src/hooks/useApi';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {classify} from 'src/utils/classify';
import {
  checkForCandidateStandardEntity,
  SUPPORTED_CHATBOT_USECASES,
} from 'src/utils/email';
import {Link} from 'src/rerouter';
import matchSorter from 'match-sorter';
import {MultiPanePanel} from 'src/components/lib/multi-pane-panel';
import {
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {
  SubTitleLarge,
  SubTitleSmall,
  BodyMedium,
  FormLabelMedium,
  SubTitleExtraSmall,
  BodyLarge,
} from '@spaced-out/ui-design-system/lib/components/Text';

import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';

import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {DebouncedSearchInput} from 'src/components/lib/genesis/debounced-search-input.jsx';
import {Pagination} from '@spaced-out/ui-design-system/lib/components/Pagination';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import {Truncate} from '@spaced-out/ui-design-system/lib/components/Truncate';

import {
  BasicSingleCell,
  TableBottomBar,
  Table,
} from '@spaced-out/ui-design-system/lib/components/Table';
// $FlowFixMe[nonstrict-import]
import {AutoTruncatedTextGenesis as AutoTruncatedText} from 'src/components/lib/truncated-text/truncated-text.jsx';
// $FlowFixMe[nonstrict-import]
import ConversationBuilder from 'src/components/conversation-builder/conversation-builder.jsx';
import {reducer, initState} from 'src/components/conversation-builder/reducer';
import {ChatbotStateContext} from 'src/components/conversation-builder/chatbot-state-context';
import {HorizontalSeparator} from 'src/components/lib/horizontal-separator/horizontal-separator.jsx';
import {LabelValue} from './module-addon-common.jsx';

import css from './chatbot-addon.css';


export const CHATBOT_ADDON_ID = 'sense-chatbot-row';
export const CHATBOT_LINK_TEXT = 'link to chatbot';

export type ChatbotType = ChatbotType;
export type ChatbotAddonPanels = ChatbotAddonPanels;

export type ChatbotBeefreeJson = {
  type: 'rowAddon',
  value: {
    name: 'chatbotRowAddon',
    columns: Array<{
      weight: number,
      modules: Array<{
        type: 'button' | 'paragraph',
        href?: string,
        text?: string,
        html?: string,
      }>,
    }>,
    metadata: {...},
  },
};

type ChatbotAddonProps = {
  onClose: () => mixed,
  onSubmit: (ChatbotTableEntry, ?boolean, ChatbotType) => mixed,
  defaultType?: ChatbotType,
  initialPanel?: ChatbotAddonPanels,
  editChatbotInfo: {
    flowId: ?number,
    chatbotType: ?string,
  },
  workflowEntityType: EntityType,
  entityMappings: EntityMappingsByEntityType,
  loadingChatbotSubmit: boolean,
};

export const useAutomationChatbot = (
  workflowEntityType: EntityType,
  entityMappings: EntityMappingsByEntityType,
  editChatbotInfo: {
    flowId: ?number,
    chatbotType: ?string,
  },
): {
  allEntries: ?Array<ChatbotTableEntry>,
  areChatbotsLoading: boolean,
  filteredChatbotEntries: Array<ChatbotTableEntry>,
  isEdit: boolean,
} => {
  const chatbotSummaryURL = React.useMemo(() => {
    if (checkForCandidateStandardEntity(workflowEntityType, entityMappings)) {
      return {pathname: 'chatbot/flows', query: {load_externals: 'False'}};
    }
    return null;
  }, [workflowEntityType]);

  const {isLoading: areChatbotsLoading, result: allEntries = []} = useApi<
    Array<ChatbotTableEntry>,
  >(chatbotSummaryURL, {
    shouldFetch: !!chatbotSummaryURL,
  });

  const filteredChatbotEntries = React.useMemo(
    () =>
      allEntries?.filter(
        ({anchor_entity_type, use_case, status}) =>
          anchor_entity_type === workflowEntityType &&
          SUPPORTED_CHATBOT_USECASES.includes(use_case) &&
          status === 'success',
      ) ?? [],
    [allEntries],
  );

  const isEdit =
    editChatbotInfo.chatbotType != null && editChatbotInfo.flowId != null;

  return {
    allEntries,
    areChatbotsLoading,
    filteredChatbotEntries,
    isEdit,
  };
};

export const ChatbotAddon = ({
  onClose,
  onSubmit,
  defaultType = 'button',
  initialPanel = 'component',
  editChatbotInfo,
  workflowEntityType,
  entityMappings,
  loadingChatbotSubmit,
}: ChatbotAddonProps): React.Node => {
  const {allEntries, areChatbotsLoading, filteredChatbotEntries, isEdit} =
    useAutomationChatbot(workflowEntityType, entityMappings, editChatbotInfo);
  const [panel, setPanel] = React.useState(() => {
    if (isEdit) {
      return 'details';
    }
    return initialPanel;
  });
  const [widgetType, setWidgetType] = React.useState(defaultType);

  const handleSubmit = (chatbotId) => {
    const selectedChatbot = filteredChatbotEntries.find(
      ({id}) => chatbotId === id,
    );
    if (selectedChatbot) {
      if (isEdit) {
        onSubmit(
          // getChatbotBeefreeJson(editChatbotInfo.chatbotType, chatbotId),
          selectedChatbot,
          isEdit,
          editChatbotInfo.chatbotType,
        );
      } else {
        onSubmit(selectedChatbot, false, widgetType);
      }
    }
  };

  const handleDetailsSelect = (panelType) => {
    setPanel(panelType);
  };
  const handleComponentTypeSelect = (type: ChatbotType) => {
    if (isEdit && editChatbotInfo.flowId) {
      const selectedChatbot = filteredChatbotEntries.find(
        ({id}) => editChatbotInfo.flowId === id,
      );
      if (selectedChatbot) {
        onSubmit(selectedChatbot, isEdit, type);
      }
    } else {
      setWidgetType(type);
      setPanel('chatbots');
    }
  };
  const handleBack = () => {
    if (isEdit) {
      setPanel('details');
    } else {
      setPanel('component');
    }
  };

  const handleBackWidget = () => {
    setPanel('details');
  };

  const getChildren = (currentPanel) => {
    if (
      currentPanel === 'details' &&
      editChatbotInfo.flowId != null &&
      editChatbotInfo.chatbotType
    ) {
      return (
        <ChatbotDetails
          flowId={editChatbotInfo.flowId}
          chatbotType={editChatbotInfo.chatbotType}
          onSelect={handleDetailsSelect}
          chatbotsLoading={areChatbotsLoading}
          chatbotEntries={allEntries}
        />
      );
    }
    if (currentPanel === 'component') {
      return (
        <WidgetConfig
          onSelect={handleComponentTypeSelect}
          onBack={isEdit ? handleBackWidget : null}
          loadingChatbotSubmit={loadingChatbotSubmit}
        />
      );
    }
    if (currentPanel === 'chatbots') {
      return (
        <ChatbotSelection
          onSubmit={handleSubmit}
          onBack={handleBack}
          flowId={editChatbotInfo.flowId}
          chatbotsLoading={areChatbotsLoading}
          chatbotEntries={filteredChatbotEntries}
          loadingChatbotSubmit={loadingChatbotSubmit}
        />
      );
    }
    return null;
  };
  return (
    <MultiPanePanel
      isOpen={true}
      onClose={onClose}
      hideBackdrop={false}
      classNames={{backdrop: css.panelBackdrop}}
    >
      {getChildren(panel)}
    </MultiPanePanel>
  );
};

export const ChatbotDetails = ({
  flowId,
  chatbotType,
  closePanel,
  onSelect,
  chatbotsLoading,
  chatbotEntries,
}: {
  flowId: number,
  chatbotType: string,
  closePanel?: () => mixed,
  onSelect: (string) => mixed,
  chatbotsLoading: boolean,
  chatbotEntries: ?Array<ChatbotTableEntry>,
}): React.Node => {
  const {t} = useLabelTranslation();
  const chatbotObject = chatbotEntries?.find(({id}) => id === flowId);
  const chatbotName = chatbotObject?.name ?? '';
  const useCase = chatbotObject?.use_case ?? '';
  const [selectedChatbot, setSelectedChatbot] = React.useState(null);

  const handlePreviewClick = (id) => {
    setSelectedChatbot(id);
  };
  const handlePreviewClose = () => {
    setSelectedChatbot(null);
  };

  if (selectedChatbot) {
    return (
      <ChatbotPreviewPanel
        chatbotId={String(flowId)}
        onClose={handlePreviewClose}
        titleText={getPreviewTitleText(flowId, chatbotEntries ?? [], t)}
      />
    );
  }
  return (
    <>
      <PanelHeader onCloseButtonClick={closePanel}>
        <SubTitleLarge>
          {t('Chatbot_AddOn_Configuration', 'Chatbot Add-On Configuration')}
        </SubTitleLarge>
      </PanelHeader>
      {chatbotsLoading ? (
        <PanelBody className={css.loaderContainer}>
          <CircularLoader size="large" />
        </PanelBody>
      ) : (
        <PanelBody>
          <Card
            paddingTop="medium"
            paddingBottom="medium"
            paddingLeft="small"
            paddingRight="small"
            classNames={{wrapper: css.detailsCard}}
          >
            <div className={css.detailsItems}>
              <LabelValue
                label={t('Chatbot_Name', 'Chatbot Name')}
                value={chatbotName}
              />
              <LabelValue label={t('Usecase', 'Usecase')} value={useCase} />

              <Button
                size="small"
                type="ghost"
                onClick={() => {
                  handlePreviewClick(flowId);
                }}
              >
                {t('Preview', 'Preview')}
              </Button>

              <Button
                type="secondary"
                iconRightName="chevron-right"
                onClick={() => onSelect('chatbots')}
              >
                {t('Edit', 'Edit')}
              </Button>
            </div>
            <HorizontalSeparator />
            <div className={css.detailsItems}>
              <LabelValue
                label={t('Display_As', 'Display As')}
                value={
                  chatbotType === 'text'
                    ? t('Text_Component', 'Text Component')
                    : t('Button', 'Button')
                }
              />
              <Button
                type="secondary"
                iconRightName="chevron-right"
                onClick={() => onSelect('component')}
              >
                {t('Edit', 'Edit')}
              </Button>
            </div>
          </Card>
        </PanelBody>
      )}
    </>
  );
};

export const WidgetConfig = ({
  onSelect,
  closePanel,
  onBack,
  loadingChatbotSubmit,
}: {
  onSelect: (ChatbotType) => mixed,
  closePanel?: () => mixed,
  onBack: ?() => mixed,
  loadingChatbotSubmit: boolean,
}): React.Node => {
  const {t} = useLabelTranslation();
  return (
    <>
      <PanelHeader onCloseButtonClick={closePanel}>
        <>
          {onBack && (
            <Button onClick={onBack} type="ghost" iconLeftName="chevron-left" />
          )}
          <SubTitleLarge>
            {t('Select_Component_Type', 'Select Component Type')}
          </SubTitleLarge>
        </>
      </PanelHeader>
      <PanelBody>
        {loadingChatbotSubmit ? (
          <div className={css.loaderContainer}>
            <CircularLoader size="large" />
            <BodyLarge>
              {t(
                'Changing_the_display_type_for_the_attached_chatbot',
                'Changing the display type for the attached chatbot',
              )}
            </BodyLarge>
          </div>
        ) : (
          <div className={css.content}>
            <SubTitleSmall>
              {t(
                'Select_the_component_to_open_chatbot_link',
                'Select the component to open chatbot link',
              )}
            </SubTitleSmall>
            <ComponentTypeItem
              iconName="rectangle-wide"
              id="button"
              onSelect={onSelect}
              title={t('Button', 'Button')}
              description={t(
                'Chatbot_will_be_opened_in_new_tab_when_button_is_clicked',
                'Chatbot will be opened in new tab when button is clicked',
              )}
            />
            <ComponentTypeItem
              iconName="text"
              id="text"
              onSelect={onSelect}
              title={t('Text_Component', 'Text Component')}
              description={t(
                'Chatbot_will_be_opened_in_new_tab_when_hyper_link_is_clicked',
                'Chatbot will be opened in new tab when hyper link is clicked',
              )}
            />
          </div>
        )}
      </PanelBody>
    </>
  );
};

const Name = ({data, extras}) => {
  const isSelected = extras?.flowId === data.id;
  return (
    <BasicSingleCell className={classify({[css.highlight]: isSelected})}>
      <BodyMedium className={css.name}>
        <AutoTruncatedText className={css.name} text={data.name} />
      </BodyMedium>
    </BasicSingleCell>
  );
};

const Usecase = ({data, extras}) => {
  const isSelected = extras?.flowId === data.id;
  return (
    <BasicSingleCell className={classify({[css.highlight]: isSelected})}>
      <BodyMedium>{data.use_case}</BodyMedium>
    </BasicSingleCell>
  );
};

const Selection = ({data, extras}) => {
  const {t} = useLabelTranslation();
  const isSelected = extras?.flowId === data.id;
  return (
    <BasicSingleCell
      className={classify(css.addonActions, {[css.highlight]: isSelected})}
    >
      <Button
        onClick={() => {
          extras?.onPreviewClick(data.id);
        }}
        size="small"
        type="ghost"
      >
        {t('Preview', 'Preview')}
      </Button>
      <Button
        onClick={() => {
          extras?.onSelect(data.id);
        }}
        size="small"
        type={isSelected ? 'tertiary' : 'secondary'}
        iconRightName="chevron-right"
      >
        {t('Select', 'Select')}
      </Button>
    </BasicSingleCell>
  );
};

export const ChatbotSelection = ({
  onSubmit,
  closePanel,
  onBack,
  flowId,
  chatbotsLoading,
  chatbotEntries: entries,
  loadingChatbotSubmit,
  footer = null,
  alert = null,
  panelHeaderText = 'Select a Chatbot',
}: {
  onSubmit: (chatbotId: number) => mixed,
  closePanel?: () => mixed,
  onBack?: () => mixed,
  flowId: ?number,
  chatbotsLoading: boolean,
  chatbotEntries: ?Array<ChatbotTableEntry>,
  loadingChatbotSubmit: boolean,
  footer?: React.Node,
  alert?: React.Node,
  panelHeaderText?: string,
}): React.Node => {
  const {t, makeKey} = useLabelTranslation();
  const headers = [
    {
      label: t('Chatbot_Name', 'Chatbot Name'),
      key: 'name',
      sortable: true,
      render: Name,
    },
    {
      label: t('Use_Case', 'Use Case'),
      key: 'use_case',
      sortable: true,
      render: Usecase,
    },
    {
      label: '',
      key: 'id',
      render: Selection,
      sortable: false,
    },
  ];
  const [query, setQuery] = React.useState('');
  const [selectedChatbot, setSelectedChatbot] = React.useState(null);
  const [useCaseFilters, setUseCaseFilters] = React.useState(new Set());

  const [pagination, setPagination] = React.useState({
    totalPages: 0,
    currentPage: 1,
    pageSize: 15,
  });

  const handleUseCaseFilterChange = ({key}) => {
    setUseCaseFilters((prevFilters) => {
      if (prevFilters.has(key)) {
        return new Set([...prevFilters].filter((filter) => filter !== key));
      }
      return new Set([...prevFilters, key]);
    });
  };
  const filterOptions = React.useMemo(() => {
    return [...new Set((entries ?? []).map((entry) => entry.use_case))].map(
      (option) => ({
        key: option,
        label: option,
      }),
    );
  }, [entries?.length]);

  const filteredEntries = React.useMemo(() => {
    if (useCaseFilters.size === 0) {
      return entries;
    }
    return entries?.filter((entry) => useCaseFilters.has(entry.use_case));
  }, [entries, useCaseFilters]);

  const matchingEntries =
    matchSorter(filteredEntries, query, {keys: ['name']}) ?? [];

  const onQueryChange = (val) => {
    setQuery(val);
  };

  const handlePreviewClick = (id: string) => {
    setSelectedChatbot(id);
  };
  const handlePreviewClose = () => {
    setSelectedChatbot(null);
  };

  React.useEffect(() => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      currentPage: 1,
      totalPages: Math.ceil(matchingEntries.length / prevPagination.pageSize),
    }));
  }, [matchingEntries.length]);

  const paginatedEntries = React.useMemo(() => {
    return matchingEntries.slice(
      (pagination.currentPage - 1) * pagination.pageSize,
      pagination.currentPage * pagination.pageSize,
    );
  }, [pagination.currentPage, pagination.pageSize, matchingEntries]);

  const selectedFilters = [...useCaseFilters.values()];

  if (selectedChatbot) {
    return (
      <ChatbotPreviewPanel
        chatbotId={selectedChatbot}
        onClose={handlePreviewClose}
        onSelect={onSubmit}
        titleText={getPreviewTitleText(
          parseInt(selectedChatbot, 10),
          entries ?? [],
          t,
        )}
        loadingChatbotSubmit={loadingChatbotSubmit}
      />
    );
  }
  return (
    <>
      <PanelHeader onCloseButtonClick={closePanel}>
        <>
          {typeof onBack === 'function' && (
            <Button onClick={onBack} type="ghost" iconLeftName="chevron-left" />
          )}
          <SubTitleLarge>
            {t(makeKey(panelHeaderText), panelHeaderText)}
          </SubTitleLarge>
        </>
      </PanelHeader>
      <PanelBody>
        <div className={css.chatbotSelectionContent}>
          {!!alert && alert}
          <Card classNames={{wrapper: css.searchTableCard}}>
            <div className={css.searchFilter}>
              <DebouncedSearchInput
                classNames={{container: css.searchContainer}}
                value={query}
                onChange={onQueryChange}
                placeholder={t('Search_for_chatbot', 'Search for chatbot')}
              />

              {filterOptions.length > 1 && (
                <ButtonDropdown
                  type="tertiary"
                  iconRightName="chevron-down"
                  iconRightType="regular"
                  menu={{
                    isFluid: false,
                    menuDisabled: false,
                    options: filterOptions,
                    optionsVariant: 'checkbox',
                    selectedKeys: selectedFilters,
                    size: 'medium',
                  }}
                  onOptionSelect={handleUseCaseFilterChange}
                  size="medium"
                  anchorPosition="bottom-end"
                >
                  {`${t('usecase', 'usecase')} (${useCaseFilters.size})`}
                </ButtonDropdown>
              )}
            </div>
            <Table
              borderRadius="0px"
              classNames={{wrapper: css.chatbotSelectionTableContainer}}
              isLoading={chatbotsLoading || loadingChatbotSubmit}
              headers={headers}
              entries={paginatedEntries}
              stickyHeader
              emptyText={
                <EmptyState
                  description={`${t(
                    'We_cant_find_any_items_matching_your_search',
                    'We cant find any items matching your search',
                  )}.`}
                  title={t('Sorry_No_result_found', 'Sorry! No result found')}
                  imageVariant="file"
                />
              }
              extras={{
                flowId,
                onPreviewClick: handlePreviewClick,
                onSelect: onSubmit,
              }}
            />
            <TableBottomBar>
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onChange={(page) => {
                  setPagination((pagination) => ({
                    ...pagination,
                    currentPage: page ?? 1,
                  }));
                }}
              >
                <SubTitleExtraSmall color="secondary">
                  {`${t('Showing_page', 'Showing page')} `}
                  {pagination.currentPage}
                  {` ${t('of', 'of')}`} {pagination.totalPages}
                </SubTitleExtraSmall>
              </Pagination>
            </TableBottomBar>
          </Card>
        </div>
      </PanelBody>
      {!!footer && <PanelFooter>{footer}</PanelFooter>}
    </>
  );
};

export const ChatbotPreviewPanel = ({
  onClose,
  chatbotId,
  onSelect,
  titleText,
  loadingChatbotSubmit,
}: {
  onClose: () => mixed,
  chatbotId: string,
  onSelect?: (flowId: number) => mixed,
  titleText?: React.Node,
  loadingChatbotSubmit?: boolean,
}): React.Node => {
  const {t} = useLabelTranslation();
  const {isLoading, result: flow} = useApi<Graph>(`chatbot/flows/${chatbotId}`);
  return (
    <>
      <PanelHeader onCloseButtonClick={onClose}>
        <>
          <Button onClick={onClose} type="ghost" iconLeftName="chevrons-left" />
          <SubTitleLarge>
            <Truncate line={1} showFullTextOnHover>
              {titleText}
            </Truncate>
            <Link to={`/conversations/${chatbotId}`} target="_blank">
              <Button type="ghost" size="small" iconLeftName="external-link" />
            </Link>
          </SubTitleLarge>
        </>
      </PanelHeader>
      <PanelBody>
        <div className={css.chatbotPreviewContent}>
          {isLoading ? (
            <CircularLoader />
          ) : flow ? (
            <Card classNames={{wrapper: css.chatbotPreviewCard}}>
              <ChatbotPreview flow={flow} />
            </Card>
          ) : (
            <></>
          )}
        </div>
      </PanelBody>
      {typeof onSelect === 'function' && (
        <PanelFooter>
          <Button onClick={onClose} type="ghost">
            {t('Go_back', 'Go back')}
          </Button>
          <Button
            onClick={() => flow && onSelect(flow.id)}
            isLoading={loadingChatbotSubmit}
          >
            {t('Use_this_chatbot', 'Use this chatbot')}
          </Button>
        </PanelFooter>
      )}
    </>
  );
};
export const ChatbotPreview = ({flow}: {flow: Graph}): React.Node => {
  const imageExportState = React.useState(false);
  const [state, dispatch] = React.useReducer(reducer, flow, initState);
  return (
    <ChatbotStateContext.Provider value={{state, dispatch}}>
      <ConversationBuilder
        key={flow.id}
        style={{flex: '1 1 auto'}}
        graph={flow}
        imageExportState={imageExportState}
        disabled
        state={state}
        dispatch={dispatch}
        isJourneyPreview
      />
    </ChatbotStateContext.Provider>
  );
};

export const ComponentTypeItem = ({
  iconName,
  onSelect,
  id,
  title,
  description,
}: {
  iconName: string,
  onSelect: (ChatbotType) => mixed,
  id: ChatbotType,
  title: React.Node,
  description: React.Node,
}): React.Node => {
  const {t} = useLabelTranslation();
  return (
    <Card
      paddingTop="small"
      paddingBottom="small"
      paddingLeft="small"
      paddingRight="small"
      classNames={{wrapper: css.componentTypeItemContainer}}
    >
      <div className={css.iconContainer}>
        <Icon name={iconName} />
      </div>

      <div>
        <FormLabelMedium>{title}</FormLabelMedium>
        <BodyMedium>{description}</BodyMedium>
      </div>
      <Button
        type="secondary"
        onClick={() => {
          onSelect(id);
        }}
        iconRightName="chevron-right"
      >
        {t('Select', 'Select')}
      </Button>
    </Card>
  );
};

export function getPreviewTitleText(
  selectedBotId: ?number,
  entries: Array<ChatbotTableEntry> = [],
  t?: (key: string, defaultMessage: string) => string,
): React.Node {
  const selectedBot = entries.find((entry) => entry.id === selectedBotId);
  if (selectedBot) {
    return `${selectedBot.name} | ${selectedBot.use_case}`;
  }
  return t ? t('Preview', 'Preview') : 'Preview';
}

function getChatbotBeefreeJson(componentType: ChatbotType, flow_id: number) {
  const identifier = `chatbot_link_${flow_id}`; // N/R
  const placeholder = `{{${identifier}}}`;

  let modules = [
    {
      // This describes the content modules inside the column
      type: 'button',
      text: CHATBOT_LINK_TEXT,
      href: placeholder,
    },
  ];
  if (componentType === 'text') {
    modules = [
      {
        type: 'paragraph',
        html: `<a href="${placeholder}">${CHATBOT_LINK_TEXT}</a>`,
      },
    ];
  }
  return {
    type: 'rowAddon',
    value: {
      name: 'chatbotRowAddon', // Identifies the row
      columns: [
        {
          // The columns inside the row
          weight: 12, // This weight identifies a single column row
          modules,
        },
      ],
      metadata: {
        flow_id,
        [identifier]: {flow_id},
        chatbotType: componentType,
      }, // an object of custom metadata that will be passed back to the Application when the content selection is triggered again
    },
  };
}
